import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAccount } from "../../context/AccountContext";
import ReconcileCard from "./ReconcileCard";

const fmt0   = (n) => n == null ? "—" : new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.abs(n));
const fmtRs  = (n) => n == null ? "—" : `₹${fmt0(n)}`;
const fmtPnl = (n) => {
  if (n == null) return "—";
  return <span style={{ color: n >= 0 ? "#28a745" : "#dc3545", fontWeight: 600 }}>
    {n >= 0 ? "+" : "-"}₹{fmt0(n)}
  </span>;
};

const today = () => new Date().toISOString().slice(0, 10);
const thirtyDaysAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
};

export default function PnLPage() {
  const { selectedAccount } = useAccount();

  const [fromDate, setFromDate]     = useState(thirtyDaysAgo());
  const [toDate, setToDate]         = useState(today());
  const [symbol, setSymbol]         = useState("");
  const [expiry, setExpiry]         = useState("");
  const [strategyType, setStratType] = useState("");

  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const [tab, setTab]       = useState("realized"); // realized | open | strategies

  const fetch = useCallback(async () => {
    if (!selectedAccount || selectedAccount === "All Accounts") return;
    setLoading(true);
    setError(null);
    try {
      const params = { username: selectedAccount };
      if (fromDate)     params.from_date     = fromDate;
      if (toDate)       params.to_date       = toDate;
      if (symbol)       params.symbol        = symbol;
      if (expiry)       params.expiry_date   = expiry;
      if (strategyType) params.strategy_type = strategyType;
      const res = await axios.get("/pnl/summary", { params });
      setData(res.data);
    } catch (e) {
      setError("Failed to load P&L data.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, fromDate, toDate, symbol, expiry, strategyType]);

  useEffect(() => { fetch(); }, [fetch]);

  const symbols  = data?.filter_options?.symbols  || [];
  const expiries = data?.filter_options?.expiries || [];
  const totals   = data?.totals || {};

  return (
    <div style={{ padding: "20px 20px 40px", background: "#f4f6f9", minHeight: "100vh" }}>

      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: 0 }}>P&amp;L Summary</h2>
        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>Realized trades, open positions and strategy accounting</p>
      </div>

      {/* ── Filters ── */}
      <div style={filterRow}>
        <FilterGroup label="From">
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={inputStyle} />
        </FilterGroup>
        <FilterGroup label="To">
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={inputStyle} />
        </FilterGroup>
        <FilterGroup label="Symbol">
          <select value={symbol} onChange={e => setSymbol(e.target.value)} style={inputStyle}>
            <option value="">All</option>
            {symbols.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </FilterGroup>
        <FilterGroup label="Expiry">
          <select value={expiry} onChange={e => setExpiry(e.target.value)} style={inputStyle}>
            <option value="">All</option>
            {expiries.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </FilterGroup>
        <FilterGroup label="Strategy">
          <select value={strategyType} onChange={e => setStratType(e.target.value)} style={inputStyle}>
            <option value="">All</option>
            <option value="PYRAMID_HEDGING">Pyramid Hedging</option>
            <option value="DELTA_HEDGED_SHORT_STRANGLE">DHSS</option>
          </select>
        </FilterGroup>
        <button onClick={fetch} disabled={loading} style={applyBtn}>
          {loading ? "Loading…" : "Apply"}
        </button>
      </div>

      {/* ── Totals strip ── */}
      {data && (
        <div style={totalsRow}>
          <Tile label="Realized P&L"    value={totals.realized_pnl}  />
          <Tile label="Unrealised"      value={totals.unrealised}    />
          <Tile label="Potential P&L"   value={totals.potential_pnl} />
          <Tile label="Net (Real + Open)" value={totals.net}         highlight />
        </div>
      )}

      {error && <div style={errorBox}>{error}</div>}

      {/* ── Tabs ── */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {[
          { id: "realized",   label: `Realized Trades (${data?.realized?.length ?? 0})` },
          { id: "open",       label: `Open Positions (${data?.open_positions?.length ?? 0})` },
          { id: "strategies", label: `By Strategy (${data?.strategies?.length ?? 0})` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            ...tabBtn,
            background: tab === t.id ? "#2c3e50" : "#fff",
            color:      tab === t.id ? "#fff"    : "#555",
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── Tables ── */}
      {!data && !loading && (
        <div style={emptyBox}>Select an account and apply filters to view P&L</div>
      )}

      {tab === "realized" && data && (
        <PnLTable
          rows={data.realized}
          columns={[
            { key: "exit_time",      label: "Date" },
            { key: "symbolname",     label: "Symbol",      bold: true },
            { key: "instrumenttype", label: "Type" },
            { key: "optiontype",     label: "CE/PE" },
            { key: "strikeprice",    label: "Strike",      fmt: v => v ? fmtRs(v) : "—" },
            { key: "expirydate",     label: "Expiry" },
            { key: "quantity",       label: "Qty" },
            { key: "entry_price",    label: "Entry",       fmt: fmtRs },
            { key: "exit_price",     label: "Exit",        fmt: fmtRs },
            { key: "direction",      label: "Direction" },
            { key: "realized_pnl",   label: "Realized P&L", fmt: fmtPnl },
          ]}
          emptyMsg="No realized trades in this period"
        />
      )}

      {tab === "open" && data && (
        <PnLTable
          rows={data.open_positions}
          columns={[
            { key: "symbolname",   label: "Symbol",        bold: true },
            { key: "optiontype",   label: "CE/PE" },
            { key: "strikeprice",  label: "Strike",        fmt: fmtRs },
            { key: "expirydate",   label: "Expiry" },
            { key: "totallots",    label: "Lots" },
            { key: "ltp",          label: "LTP",           fmt: fmtRs },
            { key: "unrealised",   label: "Unrealised",    fmt: fmtPnl },
            { key: "potential_pnl",label: "Potential P&L", fmt: fmtPnl },
            { key: "delta",        label: "Delta",         fmt: v => v?.toFixed(4) ?? "—" },
          ]}
          emptyMsg="No open option positions"
        />
      )}

      {tab === "strategies" && data && (
        <PnLTable
          rows={data.strategies}
          columns={[
            { key: "symbol",        label: "Symbol",   bold: true },
            { key: "strategy_type", label: "Strategy", fmt: v => v === "PYRAMID_HEDGING" ? "Pyramid" : "DHSS" },
            { key: "status",        label: "Status",   fmt: v => (
              <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700,
                background: v === "ACTIVE" ? "#d4edda" : "#e2e3e5",
                color:      v === "ACTIVE" ? "#155724" : "#555" }}>{v}</span>
            )},
            { key: "booked_profit",           label: "Option Realized",  fmt: fmtPnl },
            { key: "net_potential_profit",    label: "Net Potential",    fmt: fmtPnl },
            { key: "premium_collected_total", label: "Open Premium",     fmt: fmtPnl },
            { key: "net_realized_profit",     label: "Net Realized (PH)",fmt: fmtPnl },
            { key: "stock_realized_profit",   label: "Stock Profit (PH)",fmt: fmtPnl },
            { key: "current_expiry_potential_profit", label: "Expiry Potential", fmt: fmtPnl },
            { key: "effective_avg_price",     label: "Eff. Avg Price",   fmt: fmtRs },
          ]}
          emptyMsg="No strategy data"
        />
      )}
      {/* ── Broker Reconciliation ── */}
      {selectedAccount && selectedAccount !== "All Accounts" && (
        <ReconcileCard username={selectedAccount} />
      )}

    </div>
  );
}

/* ── Sub-components ── */

function FilterGroup({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <label style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase" }}>{label}</label>
      {children}
    </div>
  );
}

function Tile({ label, value, highlight }) {
  return (
    <div style={{
      background: "#fff",
      borderRadius: 10,
      padding: "12px 18px",
      flex: 1,
      minWidth: 140,
      boxShadow: highlight ? "0 0 0 2px #2c3e50" : "0 2px 8px rgba(0,0,0,0.05)",
    }}>
      <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtPnl(value)}</div>
    </div>
  );
}

function PnLTable({ rows, columns, emptyMsg }) {
  if (!rows || rows.length === 0) {
    return <div style={emptyBox}>{emptyMsg}</div>;
  }
  return (
    <div style={{ overflowX: "auto", background: "#fff", borderRadius: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#f8f9fb", borderBottom: "2px solid #e9ecef" }}>
            {columns.map(c => (
              <th key={c.key} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, color: "#6c757d", whiteSpace: "nowrap" }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
              {columns.map(c => (
                <td key={c.key} style={{ padding: "9px 12px", fontWeight: c.bold ? 700 : 400, color: "#2c3e50", whiteSpace: "nowrap" }}>
                  {c.fmt ? c.fmt(row[c.key], row) : (row[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Styles ── */
const filterRow   = { display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 18, background: "#fff", padding: "14px 18px", borderRadius: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" };
const totalsRow   = { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 18 };
const inputStyle  = { padding: "7px 10px", borderRadius: 8, border: "1px solid #ccc", fontSize: 13, minWidth: 130 };
const applyBtn    = { padding: "8px 18px", borderRadius: 8, border: "none", background: "#2c3e50", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, alignSelf: "flex-end" };
const tabBtn      = { padding: "7px 14px", borderRadius: 20, border: "1px solid #dee2e6", cursor: "pointer", fontSize: 12, fontWeight: 500 };
const emptyBox    = { background: "#fff", borderRadius: 10, padding: "40px 20px", textAlign: "center", color: "#aaa", fontSize: 14 };
const errorBox    = { background: "#fde2e2", color: "#dc3545", padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 13 };
